import { getQuizResultsTexts } from "./quiz-results-texts.js";

/**
 * @param {any} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

/**
 * @param {string | number} value
 * @returns {number}
 */
function roundToTwo(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

/**
 * @param {string | number} value
 * @returns {string}
 */
function formatPrice(value) {
  const num = roundToTwo(value);
  return `$${num % 1 === 0 ? num : num.toFixed(2)}`;
}

/**
 * Calculate discounted regimen price by discount percent.
 *
 * @param {number} fullPrice
 * @param {any} discount
 * @returns {number}
 */
function getDiscountedPrice(fullPrice, discount) {
  const discountValue = Number(discount?.value || 0);
  return fullPrice * (1 - discountValue / 100);
}

/**
 * @param {any} regimenProduct
 * @returns {any[]}
 */
function getConstituentCards(regimenProduct) {
  const texts = getQuizResultsTexts();
  const constituentsMap = texts.regimen.constituents;
  const skus = regimenProduct?.variant?.diagnostic_data?.individual_products_sku;
  if (!Array.isArray(skus) || !skus.length) return [];

  const cards = [];
  for (const sku of skus) {
    if (!constituentsMap[sku]?.card) {
      // Keep close behavior to Angular: if unknown SKU appears, stop constituent rendering.
      return [];
    }
    cards.push(constituentsMap[sku].card);
  }
  return cards;
}

/**
 * @param {any} regimenProduct
 * @returns {string}
 */
function getBundleSlideHtml(regimenProduct) {
  const texts = getQuizResultsTexts();
  const routineSteps = Array.isArray(texts.regimen.routineSteps) ? texts.regimen.routineSteps : [];
  const affirmations = Array.isArray(texts.regimen.affirmations) ? texts.regimen.affirmations : [];
  const mainImage = regimenProduct?.media?.[0]?.src || "";

  return `
    <div class="swiper-slide">
      <div class="quiz-results-regimen-box-slide-item regimen-box-slide-bundle">
        <ul class="regimen-box-slide-bundle-step-list">
          ${routineSteps.map(
            (step) => `
              <li class="regimen-box-slide-bundle-step">
                <img src="${step.image}" alt="${escapeHtml(step.imageAlt)}">
                <span>${escapeHtml(step.text)}</span>
              </li>
            `,
          ).join("")}
        </ul>
        <ul class="regimen-box-slide-bundle-affirmation-list">
          ${affirmations.map(
            (item) => `
              <li class="regimen-box-slide-bundle-affirmation">
                <img src="${item.image}" alt="${escapeHtml(item.text)}">
                <span>${escapeHtml(item.text)}</span>
              </li>
            `,
          ).join("")}
        </ul>
        <img src="${mainImage}" alt="" class="regimen-box-slide-bundle-image">
      </div>
    </div>
  `.trim();
}

/**
 * @param {any[]} cards
 * @returns {string}
 */
function getConstituentSlidesHtml(cards) {
  const texts = getQuizResultsTexts();
  const activeIngredientLabel = texts.regimen.activeIngredientLabel;
  return cards
    .map((card) => {
      const activeIngredients = Array.isArray(card.active_ingredients)
        ? card.active_ingredients
        : [];
      return `
        <div class="swiper-slide">
          <div class="quiz-results-regimen-box-slide-item regimen-box-slide-product">
            <div class="regimen-box-slide-product-title">${escapeHtml(card.title)}</div>
            <div class="regimen-box-slide-product-subtitle">${escapeHtml(card.label)}</div>
            <ul class="regimen-box-slide-product-advantages">
              ${(card.bullets || [])
                .map((/** @type {any} */ bullet) => `<li>${escapeHtml(bullet)}</li>`)
                .join("")}
            </ul>
            ${
              activeIngredients.length
                ? `
                  <div class="regimen-box-slide-product-ingredients-wrapper">
                    <div class="regimen-box-slide-product-ingredients-title">
                      ${escapeHtml(activeIngredientLabel)}${activeIngredients.length > 1 ? "s" : ""}
                    </div>
                    <ul class="regimen-box-slide-product-ingredients-list">
                      ${activeIngredients
                        .map(
                          (/** @type {any} */ ingredient) =>
                            `<li class="regimen-box-slide-product-ingredients">${escapeHtml(ingredient)}</li>`,
                        )
                        .join("")}
                    </ul>
                  </div>
                `
                : ""
            }
            <div class="regimen-box-slide-product-image">
              <img src="${card?.images?.mobile || ""}" alt="">
            </div>
          </div>
        </div>
      `.trim();
    })
    .join("");
}

/**
 * Render regimen section header and slider content.
 * This function assumes the section markup from `quiz-results-regimen-box.liquid`.
 *
 * @param {Element} rootElement
 * @param {any} regimenProduct
 */
export function renderRegimenSliderSection(rootElement, regimenProduct) {
  if (!rootElement || !regimenProduct) return;

  const titleElement = rootElement.querySelector(".quiz-results-regimen-box-title");
  const sliderWrapper = rootElement.querySelector(
    ".js-quiz-results-regimen-box-slider .swiper-wrapper",
  );

  if (!titleElement || !sliderWrapper) return;

  titleElement.textContent = regimenProduct.title || "";

  const cards = getConstituentCards(regimenProduct);
  const sliderHtml = getBundleSlideHtml(regimenProduct) + getConstituentSlidesHtml(cards);
  sliderWrapper.innerHTML = sliderHtml;

  // Reinitialize swiper instance after dynamic content injection.
  const slider = rootElement.querySelector(".js-quiz-results-regimen-box-slider");
  const sliderElement = /** @type {any} */ (slider);
  const swiperCtor = /** @type {any} */ (window).Swiper;
  if (!sliderElement || typeof swiperCtor === "undefined") return;

  if (sliderElement.swiper) {
    sliderElement.swiper.destroy(true, true);
  }

  // Match the existing slider settings in theme.js.
  new swiperCtor(sliderElement, {
    slidesPerView: 1,
    centeredSlides: false,
    loop: true,
    speed: 500,
    spaceBetween: 10,
    navigation: {
      nextEl: sliderElement.querySelector(".quiz-results-regimen-box-slider-button-next"),
      prevEl: sliderElement.querySelector(".quiz-results-regimen-box-slider-button-prev"),
    },
  });
}

/**
 * Update regimen price label according to active discount state.
 * If discount exists: <s>old_price</s> new_price
 * If no discount: new_price only
 *
 * @param {Element} rootElement
 * @param {any} regimenProduct
 * @param {any} discount
 */
export function renderRegimenPriceLabel(rootElement, regimenProduct, discount) {
  if (!rootElement || !regimenProduct) return;
  const priceSuffix = getQuizResultsTexts().regimen.priceSuffix;

  const priceElement = rootElement.querySelector(".quiz-results-regimen-box-label");
  if (!priceElement) return;

  const fullPrice = Number(regimenProduct?.variant?.price || 0);
  const discountedPrice = getDiscountedPrice(fullPrice, discount);
  const oldPrice = formatPrice(fullPrice);
  const newPrice = formatPrice(discountedPrice);

  if (discountedPrice < fullPrice) {
    priceElement.innerHTML = `<s>${oldPrice}</s> ${newPrice} ${escapeHtml(priceSuffix)}`;
    return;
  }

  priceElement.textContent = `${newPrice} ${priceSuffix}`;
}
